import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../scripts/AuthContext";
import { useOrgFilter } from "../scripts/OrgFilterContext";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API_BASE_URL } from "@/lib/apiBase";
import { resolveAvatarUrl } from "@/lib/avatarApi";
import { toastError, toastSuccess } from "@/lib/toast";
import { pathWithPostAnchor } from "@/lib/permalinks";
import { useSocialPaths } from "@/lib/socialPaths";
import PostCard from "./PostCard";
import OrganizationAvatar from "@/components/avatars/OrganizationAvatar";
import VerifiedOrgMark from "./VerifiedOrgMark";

/**
 * Public Circa Social org profile — /org/:handle
 * Circa Social workspace: follow as yourself.
 * Org workplace: follow as the org (never self-follow own profile).
 */
export default function SocialOrgProfilePage() {
  const { handle } = useParams();
  const { accessToken, user } = useAuth();
  const { selectedOrgId } = useOrgFilter();
  const navigate = useNavigate();
  const location = useLocation();
  const paths = useSocialPaths();

  const isSocialWorkspace =
    selectedOrgId === "social" ||
    selectedOrgId === null ||
    selectedOrgId === "";
  const hasOrgSelected = !isSocialWorkspace;

  const [profile, setProfile] = useState(null);
  const [orgRelation, setOrgRelation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);

  const loadProfile = useCallback(async () => {
    if (!accessToken || !handle) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/social/orgs/${encodeURIComponent(handle)}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setProfile(res.data);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to load profile"
      );
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken, handle]);

  const loadOrgRelation = useCallback(async () => {
    if (!accessToken || !handle || !hasOrgSelected) {
      setOrgRelation(null);
      return;
    }
    try {
      const res = await axios.get(
        `${API_BASE_URL}/org/${encodeURIComponent(selectedOrgId)}/social/orgs/${encodeURIComponent(handle)}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setOrgRelation(res.data);
    } catch (err) {
      console.error(err);
      setOrgRelation({
        is_following: false,
        can_follow: false,
        is_self: false,
      });
    }
  }, [accessToken, handle, hasOrgSelected, selectedOrgId]);

  const loadPosts = useCallback(async () => {
    if (!accessToken || !handle) return;
    setPostsLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/social/orgs/${encodeURIComponent(handle)}/posts`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setPosts(
        (res.data?.posts || []).map((p) => ({
          ...p,
          post_id: p.social_post_id ?? p.post_id,
        }))
      );
    } catch (err) {
      console.error(err);
      setPosts([]);
    } finally {
      setPostsLoading(false);
    }
  }, [accessToken, handle]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    loadOrgRelation();
  }, [loadOrgRelation]);

  useEffect(() => {
    if (tab === "posts") loadPosts();
  }, [tab, loadPosts]);

  const isOwnOrgProfile = Boolean(hasOrgSelected && orgRelation?.is_self);
  const isFollowing = hasOrgSelected
    ? Boolean(orgRelation?.is_following)
    : Boolean(profile?.is_following);
  const canFollow = hasOrgSelected
    ? Boolean(orgRelation?.can_follow) && !isOwnOrgProfile
    : true;

  const toggleFollow = async () => {
    if (!profile || busy || isOwnOrgProfile) return;
    if (hasOrgSelected && !canFollow) return;
    setBusy(true);
    try {
      if (hasOrgSelected) {
        const url = `${API_BASE_URL}/org/${encodeURIComponent(
          selectedOrgId
        )}/social/follow/orgs/${encodeURIComponent(handle)}`;
        if (isFollowing) {
          await axios.delete(url, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          setOrgRelation((prev) => ({
            ...(prev || {}),
            is_following: false,
            can_follow: true,
            is_self: false,
          }));
        } else {
          await axios.post(
            url,
            {},
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          setOrgRelation((prev) => ({
            ...(prev || {}),
            is_following: true,
            can_follow: true,
            is_self: false,
          }));
        }
        toastSuccess(isFollowing ? "Unfollowed" : "Following");
      } else {
        const method = isFollowing ? "delete" : "post";
        const res = await axios({
          method,
          url: `${API_BASE_URL}/social/orgs/${encodeURIComponent(handle)}/follow`,
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setProfile(res.data);
        toastSuccess(isFollowing ? "Unfollowed" : "Following");
      }
    } catch (err) {
      toastError(err, "Could not update follow");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <Alert variant="destructive">
          <AlertDescription>{error || "Not found"}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const orgForAvatar = {
    org_id: profile.org_id,
    name: profile.display_name,
    avatar_url: profile.avatar_url,
  };

  return (
    <div className="pb-16">
      <div className="border-b border-border bg-muted/30">
        <div
          className="h-28 w-full bg-gradient-to-br from-primary/25 via-muted to-background sm:h-36"
          style={
            profile.banner_url
              ? {
                  backgroundImage: `url(${resolveAvatarUrl(profile.banner_url)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }
              : undefined
          }
        />
        <div className="mx-auto max-w-2xl min-w-0 px-4 -mt-10 pb-4">
          <div className="flex min-w-0 flex-wrap items-end justify-between gap-4">
            <OrganizationAvatar
              org={orgForAvatar}
              className="h-24 w-24 shrink-0 ring-4 ring-background"
              fallbackClassName="text-2xl"
            />
            <div className="pb-1">
              {isOwnOrgProfile ? (
                <Button type="button" variant="outline" asChild>
                  <Link
                    to={
                      paths.isOrgActor
                        ? paths.profileEdit()
                        : `/orgs/${encodeURIComponent(selectedOrgId)}/social/profile`
                    }
                  >
                    Manage profile
                  </Link>
                </Button>
              ) : hasOrgSelected && !canFollow ? (
                <p className="text-xs text-muted-foreground max-w-[14rem] text-right">
                  Viewing as organization. Follow requires Social publish.
                </p>
              ) : (
                <Button
                  type="button"
                  variant={isFollowing ? "outline" : "default"}
                  disabled={busy}
                  onClick={toggleFollow}
                >
                  {busy ? "…" : isFollowing ? "Following" : "Follow"}
                </Button>
              )}
            </div>
          </div>
          <div className="mt-3 min-w-0 space-y-1">
            <h1 className="flex min-w-0 items-center gap-2 text-2xl font-semibold tracking-tight">
              <span className="truncate" title={profile.display_name}>
                {profile.display_name}
              </span>
              <VerifiedOrgMark
                org={profile}
                className="h-5 w-5 shrink-0"
              />
            </h1>
            <p className="truncate text-muted-foreground" title={`@${profile.username}`}>
              @{profile.username}
            </p>
            {isOwnOrgProfile ? (
              <p className="text-sm text-muted-foreground pt-1">
                This is your organization’s Circa Social profile.
              </p>
            ) : null}
            {profile.bio ? (
              <p className="text-sm whitespace-pre-wrap pt-1">{profile.bio}</p>
            ) : null}
            <p className="text-sm text-muted-foreground pt-1">
              <span className="font-medium text-foreground">
                {profile.following_count ?? 0}
              </span>{" "}
              Following
              <span className="mx-2">·</span>
              <span className="font-medium text-foreground">
                {profile.followers_count ?? 0}
              </span>{" "}
              Followers
              <span className="mx-2">·</span>
              <span className="font-medium text-foreground">
                {profile.posts_count ?? 0}
              </span>{" "}
              Posts
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-2xl min-w-0 px-4 mt-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 max-w-full h-auto flex-wrap">
            <TabsTrigger value="posts">Posts</TabsTrigger>
            <TabsTrigger value="communities">Communities</TabsTrigger>
            <TabsTrigger value="about">About</TabsTrigger>
          </TabsList>

          <TabsContent value="posts" className="space-y-4 mt-0">
            {postsLoading && posts.length === 0 ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : posts.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                No profile posts yet.
              </p>
            ) : (
              posts.map((post) => (
                <PostCard
                  key={post.post_id}
                  post={post}
                  currentUserId={user?.user_id}
                  authorAboveTitle
                  socialHome
                  relativeTimestamps
                  socialTombstone
                  socialRepost
                  accessToken={accessToken}
                  onExpand={() => {
                    const returnTo = pathWithPostAnchor(
                      `${location.pathname}${location.search || ""}`,
                      post.post_id
                    );
                    navigate(
                      paths.thread({
                        postId: post.post_id,
                        returnPath: returnTo,
                      })
                    );
                  }}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="communities" className="space-y-3 mt-0">
            {(profile.communities || []).length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                No communities yet.
              </p>
            ) : (
              (profile.communities || []).map((c) => (
                <Link
                  key={c.community_id}
                  to={paths.community(c.community_id)}
                  className="block rounded-lg border border-border p-4 hover:bg-muted/40 transition-colors"
                >
                  <p className="font-medium">{c.display_name}</p>
                  {c.description ? (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {c.description}
                    </p>
                  ) : null}
                  {c.is_primary_for_org ? (
                    <p className="text-xs text-muted-foreground mt-2">
                      Primary community
                    </p>
                  ) : null}
                </Link>
              ))
            )}
          </TabsContent>

          <TabsContent value="about" className="mt-0 space-y-2 text-sm">
            {profile.location ? (
              <p>
                <span className="text-muted-foreground">Location · </span>
                {profile.location}
              </p>
            ) : null}
            {profile.website_url ? (
              <p>
                <span className="text-muted-foreground">Website · </span>
                <a
                  href={profile.website_url}
                  className="text-primary hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  {profile.website_url}
                </a>
              </p>
            ) : null}
            {!profile.location && !profile.website_url && !profile.bio ? (
              <p className="text-muted-foreground py-8 text-center">
                No about details yet.
              </p>
            ) : null}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
